import { useState } from 'react'
import { useSwipeable } from 'react-swipeable'

type UseSwipeNavigationProps = {
  onPageChange: (page: number) => void
  currentPage: number
  lastPage?: number
  /** trueの場合、スワイプUI・遷移を完全に無効化 */
  disabled?: boolean
}

type UseSwipeNavigationReturn = {
  swipeHandler: ReturnType<typeof useSwipeable>
  swipeProgress: number
  swipeDirection: 'left' | 'right' | null
}

export const useSwipeNavigation = ({
  onPageChange,
  currentPage,
  lastPage,
  disabled,
}: UseSwipeNavigationProps): UseSwipeNavigationReturn => {
  const [swipeProgress, setSwipeProgress] = useState(0)
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null)

  const canGoLeft = !disabled && currentPage > 1
  const canGoRight = !disabled && (!lastPage || currentPage < lastPage)

  const swipeHandler = useSwipeable({
    onSwiping: (eventData) => {
      if (disabled) return
      // 水平方向の移動が垂直より大きい場合のみスワイプとして扱う
      if (Math.abs(eventData.deltaX) <= Math.abs(eventData.deltaY)) return
      const direction = eventData.deltaX > 0 ? 'right' : 'left'
      // 遷移できない方向ではUIを表示しない
      if (direction === 'right' && !canGoLeft) return
      if (direction === 'left' && !canGoRight) return
      const progress = Math.abs(eventData.deltaX) / (window.innerWidth / 2)
      setSwipeProgress(Math.min(progress, 1))
      setSwipeDirection(direction)
    },
    onSwipedRight: (eventData) => {
      if (canGoLeft && Math.abs(eventData.deltaX) >= window.innerWidth / 2) {
        onPageChange(currentPage - 1)
      }
      setSwipeProgress(0)
      setSwipeDirection(null)
    },
    onSwipedLeft: (eventData) => {
      if (canGoRight && Math.abs(eventData.deltaX) >= window.innerWidth / 2) {
        onPageChange(currentPage + 1)
      }
      setSwipeProgress(0)
      setSwipeDirection(null)
    },
    onTouchEndOrOnMouseUp: () => {
      setSwipeProgress(0)
      setSwipeDirection(null)
    },
    delta: 30, // 縦スクロールを邪魔しないよう閾値を上げる
    preventScrollOnSwipe: false, // 縦スクロールをブロックしない
    trackMouse: false,
    trackTouch: true,
  })

  return {
    swipeHandler,
    swipeProgress,
    swipeDirection,
  }
}
